import { Link, useNavigate } from "@tanstack/react-router";
import { Mail, Lock, Eye, EyeOff, LogIn } from "lucide-react";
import { useState } from "react";
import { toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { Input, Label, Checkbox } from "@/shared/components";
import { AuthTemplate } from "@modootoday/ui-widgets/templates";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState({ terms: false, privacy: false });
  const [loading, setLoading] = useState(false);

  const canSubmit = email && password && agree.terms && agree.privacy;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast.success("로그인 되었습니다");

      const next = new URLSearchParams(window.location.search).get("next");
      if (next && !next.startsWith("/auth/")) {
        window.location.assign(next);
      } else {
        navigate({ to: "/dashboard" });
      }
    } catch (err: any) {
      toast.error(err.message || "로그인에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: "naver" | "kakao") => {
    try {
      const next = new URLSearchParams(window.location.search).get("next");
      const redirectTo = new URL(`${window.location.origin}/auth/callback`);
      if (next) {
        redirectTo.searchParams.set("next", next);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: redirectTo.toString(),
        },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(`${provider} 로그인 중 오류가 발생했습니다: ${err.message}`);
    }
  };

  return (
    <AuthTemplate title="로그인" subtitle="데이터랩툴즈에 오신 것을 환영합니다">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="mb-1.5 block">이메일</Label>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            leadingIcon={<Mail className="size-4" />}
          />
        </div>

        <div>
          <Label className="mb-1.5 block">비밀번호</Label>
          <Input
            type={showPw ? "text" : "password"}
            placeholder="비밀번호를 입력하세요"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
            leadingIcon={<Lock className="size-4" />}
            trailingIcon={
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 표시"}
                className="text-text-tertiary hover:text-text-primary"
              >
                {showPw ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </button>
            }
          />
        </div>

        <div className="space-y-2 rounded-lg bg-surface-sunken/50 p-3">
          <label className="flex items-center gap-2 text-[12px]">
            <Checkbox
              checked={agree.terms}
              onCheckedChange={(checked: boolean) =>
                setAgree((p) => ({ ...p, terms: !!checked }))
              }
            />
            <span className="text-text-tertiary">
              <a
                href="/docs/terms"
                className="font-semibold text-text-primary underline-offset-2 hover:underline"
              >
                이용약관
              </a>
              에 동의합니다
            </span>
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <Checkbox
              checked={agree.privacy}
              onCheckedChange={(checked: boolean) =>
                setAgree((p) => ({ ...p, privacy: !!checked }))
              }
            />
            <span className="text-text-tertiary">
              <a
                href="/docs/privacy"
                className="font-semibold text-text-primary underline-offset-2 hover:underline"
              >
                개인정보 처리방침
              </a>
              에 동의합니다
            </span>
          </label>
        </div>

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <span className="size-4 animate-spin rounded-full border-2 border-text-inverse/30 border-t-primary-foreground" />
          ) : (
            <LogIn className="size-4" />
          )}
          {loading ? "로그인 중..." : "로그인"}
        </button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-stroke-subtle" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-surface-raised px-3 text-[11px] text-text-tertiary">
              또는
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleSocialLogin("naver")}
            className="flex items-center justify-center gap-2 rounded-lg border border-stroke-subtle py-2.5 text-[12px] font-medium text-text-tertiary transition-colors hover:bg-surface-sunken"
          >
            <span className="flex size-5 items-center justify-center rounded bg-[#03C75A] text-[10px] font-black text-white">
              N
            </span>
            N 로그인
          </button>
          <button
            type="button"
            onClick={() => handleSocialLogin("kakao")}
            className="flex items-center justify-center gap-2 rounded-lg border border-stroke-subtle py-2.5 text-[12px] font-medium text-text-tertiary transition-colors hover:bg-surface-sunken"
          >
            <span className="flex size-5 items-center justify-center rounded bg-[#FEE500] text-[10px] font-black text-[#3C1E1E]">
              K
            </span>
            카카오
          </button>
        </div>
      </form>

      <div className="mt-6 flex items-center justify-between text-[12px] text-text-tertiary">
        <Link to="/auth/signup" className="font-medium hover:text-text-primary">
          회원가입
        </Link>
        <Link to="/auth/forgot-password" className="hover:text-text-primary">
          비밀번호를 잊으셨나요?
        </Link>
      </div>
    </AuthTemplate>
  );
}
